import type { CSSProperties, JSX } from 'react';
import type { ShellHydration } from '../../../api.js';
import type { Palette } from '../../../_theme/palette.js';
import type { SurveyQuestion } from '../types.js';
import { usePipe } from './PipingProvider.js';
import { QuestionField } from './QuestionField.js';
import { QuestionMediaBlock } from './QuestionMediaBlock.js';

interface QuestionCardProps {
  palette: Palette;
  question: SurveyQuestion;
  indexOnPage: number;
  value: unknown;
  onChange: (value: unknown) => void;
  hydration: ShellHydration;
  fieldError?: string | null;
  showNumber?: boolean;
}

const RED = '#ef4444';

export function QuestionCard({
  palette,
  question,
  indexOnPage,
  value,
  onChange,
  hydration,
  fieldError,
  showNumber = true,
}: QuestionCardProps): JSX.Element {
  const pipe = usePipe();
  const numberVisible = showNumber && question.showNumber !== false;
  return (
    <div style={cardStyle(palette)}>
      <label style={labelStyle(palette)}>
        <span>
          {numberVisible ? `${indexOnPage + 1}. ` : ''}
          {pipe(question.label)}
        </span>
        {question.required && <span style={{ color: RED, marginLeft: 4 }}>*</span>}
      </label>
      {question.description && <p style={descriptionStyle(palette)}>{pipe(question.description)}</p>}
      {question.media && <QuestionMediaBlock media={question.media} radius={palette.radius} />}
      <div style={{ marginTop: 12 }}>
        <QuestionField
          palette={palette}
          question={question}
          value={value}
          onChange={onChange}
          hydration={hydration}
        />
      </div>
      {fieldError && <p style={errorStyle()}>{fieldError}</p>}
    </div>
  );
}

function cardStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 16,
    borderRadius: palette.radius,
    border: `1px solid ${palette.border}`,
    background: palette.card,
  };
}

function labelStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 15,
    fontWeight: 500,
    color: palette.text,
    lineHeight: 1.4,
  };
}

function descriptionStyle(palette: Palette): CSSProperties {
  return { margin: 0, fontSize: 12, color: palette.textMuted, lineHeight: 1.5 };
}

function errorStyle(): CSSProperties {
  return { margin: '6px 0 0', color: RED, fontSize: 12, lineHeight: 1.4 };
}
